import {
  ContentInfo,
  SignedData,
  type SignerInfo,
} from "@peculiar/asn1-cms";
import { AsnConvert, OctetString } from "@peculiar/asn1-schema";
import { TSTInfo, TimeStampResp } from "@peculiar/asn1-tsp";
import { Certificate } from "@peculiar/asn1-x509";
import type { TSAEntry } from "@filevouch/receipt";
import { TRUST_ROOTS } from "./trust-roots";

// SHA-256 algorithm OID (RFC 5754)
const OID_SHA256 = "2.16.840.1.101.3.4.2.1";

// CMS / PKCS#9 attribute OIDs
const OID_MESSAGE_DIGEST = "1.2.840.113549.1.9.4";

// Signature algorithm OIDs
const OID_RSA_ENCRYPTION = "1.2.840.113549.1.1.1";
const OID_SHA256_RSA = "1.2.840.113549.1.1.11";
const OID_SHA384_RSA = "1.2.840.113549.1.1.12";
const OID_SHA512_RSA = "1.2.840.113549.1.1.13";
const OID_ECDSA_SHA256 = "1.2.840.10045.4.3.2";
const OID_ECDSA_SHA384 = "1.2.840.10045.4.3.3";

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToHex(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

// peculiar/asn1-* represents INTEGERs as ArrayBuffer (big-endian, raw bytes)
// once they exceed JS safe-int range. The TSA nonce is 64-bit, so we always
// land on the buffer path. Convert to a non-negative BigInt.
function nonceToBigInt(
  raw: bigint | number | ArrayBuffer | Uint8Array | undefined,
): bigint | null {
  if (raw === undefined) return null;
  if (typeof raw === "bigint") return raw;
  if (typeof raw === "number") return BigInt(raw);
  const bytes =
    raw instanceof Uint8Array ? raw : new Uint8Array(raw as ArrayBuffer);
  let v = 0n;
  for (const b of bytes) v = (v << 8n) | BigInt(b);
  return v;
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// Convert an ECDSA signature from CMS-style DER `SEQUENCE { r INTEGER, s
// INTEGER }` into Web Crypto's raw form `r || s` (each padded left to
// `curveBytes`). Required because crypto.subtle.verify("ECDSA", ...) expects
// raw, not DER. Without this, every ECDSA TSA (Apple, etc.) silently fails.
function derEcdsaToRaw(sig: Uint8Array, curveBytes: number): Uint8Array {
  if (sig[0] !== 0x30) throw new Error("ECDSA sig: not a SEQUENCE");
  let i = 1;
  let seqLen = sig[i++]!;
  if (seqLen >= 0x80) {
    const lenBytes = seqLen & 0x7f;
    seqLen = 0;
    for (let j = 0; j < lenBytes; j++) seqLen = (seqLen << 8) | sig[i++]!;
  }
  if (sig[i++] !== 0x02) throw new Error("ECDSA sig: expected INTEGER for r");
  const rLen = sig[i++]!;
  let rStart = i;
  const rEnd = i + rLen;
  while (rEnd > rStart && sig[rStart] === 0x00) rStart++;
  i = rEnd;
  if (sig[i++] !== 0x02) throw new Error("ECDSA sig: expected INTEGER for s");
  const sLen = sig[i++]!;
  let sStart = i;
  const sEnd = i + sLen;
  while (sEnd > sStart && sig[sStart] === 0x00) sStart++;
  const out = new Uint8Array(curveBytes * 2);
  out.set(sig.subarray(rStart, rEnd), curveBytes - (rEnd - rStart));
  out.set(sig.subarray(sStart, sEnd), curveBytes * 2 - (sEnd - sStart));
  return out;
}

// Wrap a payload in a DER tag/length envelope.
function derWrap(tag: number, inner: Uint8Array): Uint8Array {
  let lenBytes: Uint8Array;
  const n = inner.length;
  if (n < 0x80) {
    lenBytes = new Uint8Array([n]);
  } else {
    const bytes: number[] = [];
    let v = n;
    while (v > 0) {
      bytes.unshift(v & 0xff);
      v >>>= 8;
    }
    lenBytes = new Uint8Array([0x80 | bytes.length, ...bytes]);
  }
  const out = new Uint8Array(1 + lenBytes.length + n);
  out[0] = tag;
  out.set(lenBytes, 1);
  out.set(inner, 1 + lenBytes.length);
  return out;
}

// Map (signatureAlgorithm OID, digestAlgorithm OID) → Web Crypto algorithm
// spec for crypto.subtle.verify. Returns null if unsupported.
//
// Coverage today:
//   - RSA-PKCS1-v1_5 with SHA-256, SHA-384, SHA-512
//   - bare rsaEncryption with SHA-256 digest (cms-only path)
//   - ECDSA P-256 with SHA-256, P-384 with SHA-384
// Out of scope: SHA-1 (deprecated), SHA-512 ECDSA (P-521), RSA-PSS.
// All TSAs in the current pool (DigiCert / Apple) sign with one of the
// supported algorithms.
function getVerifyAlgorithm(
  sigOid: string,
  digestOid: string,
):
  | { algorithm: { name: string; hash?: string }; importHash: string; curveBytes?: number }
  | null {
  // Algorithm OIDs that bake in the hash
  if (sigOid === OID_SHA256_RSA) {
    return {
      algorithm: { name: "RSASSA-PKCS1-v1_5" },
      importHash: "SHA-256",
    };
  }
  if (sigOid === OID_SHA384_RSA) {
    return {
      algorithm: { name: "RSASSA-PKCS1-v1_5" },
      importHash: "SHA-384",
    };
  }
  if (sigOid === OID_SHA512_RSA) {
    return {
      algorithm: { name: "RSASSA-PKCS1-v1_5" },
      importHash: "SHA-512",
    };
  }
  // bare rsaEncryption: digestAlgorithm dictates the hash (CMS path only)
  if (sigOid === OID_RSA_ENCRYPTION && digestOid === OID_SHA256) {
    return {
      algorithm: { name: "RSASSA-PKCS1-v1_5" },
      importHash: "SHA-256",
    };
  }
  // ECDSA variants — Web Crypto needs the curve at importKey time, which
  // we get separately from the SPKI's algorithm parameters; the hash hint
  // here is what crypto.subtle.verify uses.
  if (sigOid === OID_ECDSA_SHA256) {
    return {
      algorithm: { name: "ECDSA", hash: "SHA-256" },
      importHash: "SHA-256",
      curveBytes: 32,
    };
  }
  if (sigOid === OID_ECDSA_SHA384) {
    return {
      algorithm: { name: "ECDSA", hash: "SHA-384" },
      importHash: "SHA-384",
      curveBytes: 48,
    };
  }
  return null;
}

// Verify a single cert's signature was produced by `issuer`'s public key.
// Reuses getVerifyAlgorithm for the algorithm spec; trust-root chain math
// only sees the same RSA-PKCS1 / ECDSA P-256 + SHA-256 surface as the
// SignedData verification path.
async function verifyCertSignedBy(
  cert: Certificate,
  issuer: Certificate,
): Promise<boolean> {
  const sigOid = cert.signatureAlgorithm.algorithm;
  const algoSpec = getVerifyAlgorithm(sigOid, "");
  if (!algoSpec) return false;

  const tbsBytes = AsnConvert.serialize(cert.tbsCertificate);
  const issuerSpki = AsnConvert.serialize(
    issuer.tbsCertificate.subjectPublicKeyInfo,
  );
  let key: CryptoKey;
  try {
    key = await crypto.subtle.importKey(
      "spki",
      issuerSpki,
      algoSpec.algorithm.name === "RSASSA-PKCS1-v1_5"
        ? { name: "RSASSA-PKCS1-v1_5", hash: algoSpec.importHash }
        : {
            name: "ECDSA",
            namedCurve: algoSpec.curveBytes === 48 ? "P-384" : "P-256",
          },
      false,
      ["verify"],
    );
  } catch {
    return false;
  }

  // Certificate.signatureValue is a BIT STRING; peculiar exposes it as an
  // ArrayBuffer directly (no .buffer wrapper, unlike OCTET STRING fields).
  let sigBytes: Uint8Array<ArrayBuffer> = new Uint8Array(
    (cert.signatureValue as ArrayBuffer).slice(0),
  );
  if (algoSpec.algorithm.name === "ECDSA") {
    try {
      sigBytes = derEcdsaToRaw(
        sigBytes,
        algoSpec.curveBytes ?? 32,
      ) as Uint8Array<ArrayBuffer>;
    } catch {
      return false;
    }
  }
  try {
    return await crypto.subtle.verify(
      algoSpec.algorithm.name === "RSASSA-PKCS1-v1_5"
        ? { name: "RSASSA-PKCS1-v1_5" }
        : { name: "ECDSA", hash: algoSpec.importHash },
      key,
      sigBytes as BufferSource,
      tbsBytes as BufferSource,
    );
  } catch {
    return false;
  }
}

// Pre-parse embedded trust roots and capture each one's SPKI + subject DN
// for matching. Done once per process; cached. Bad bytes (shouldn't happen
// if the const file is well-formed) are skipped quietly.
type ParsedTrustRoot = {
  name: string;
  cert: Certificate;
  spki: Uint8Array;
  subjectDn: Uint8Array;
};
let cachedRoots: ParsedTrustRoot[] | null = null;
function parsedTrustRoots(): ParsedTrustRoot[] {
  if (cachedRoots) return cachedRoots;
  cachedRoots = [];
  for (const r of TRUST_ROOTS) {
    try {
      const cert = AsnConvert.parse(r.derBytes, Certificate);
      cachedRoots.push({
        name: r.name,
        cert,
        spki: new Uint8Array(
          AsnConvert.serialize(cert.tbsCertificate.subjectPublicKeyInfo),
        ),
        subjectDn: new Uint8Array(
          AsnConvert.serialize(cert.tbsCertificate.subject),
        ),
      });
    } catch {
      // skip malformed embedded root
    }
  }
  return cachedRoots;
}

// Walk the cert chain from `signer` upward through `pool`, verifying each
// link's signature. Stops successfully when:
//   (a) the current cert IS one of our embedded trust roots (SPKI match),
//       OR
//   (b) the current cert's issuer DN matches an embedded trust root's
//       subject DN AND the trust root's public key verifies the cert's
//       signature.
// Both cases mean "chain reached an embedded trust anchor".
async function validateChain(
  signer: Certificate,
  pool: Certificate[],
): Promise<{ ok: true; anchor: string } | { ok: false; reason: string }> {
  const chain: Certificate[] = [signer];
  const MAX_DEPTH = 8;
  const roots = parsedTrustRoots();

  while (chain.length <= MAX_DEPTH) {
    const current = chain[chain.length - 1]!;

    // Case (a): current IS a trust root (matched by SPKI).
    const currentSpki = new Uint8Array(
      AsnConvert.serialize(current.tbsCertificate.subjectPublicKeyInfo),
    );
    for (const root of roots) {
      if (bytesEqual(currentSpki, root.spki)) {
        return { ok: true, anchor: root.name };
      }
    }

    const currentIssuerDn = new Uint8Array(
      AsnConvert.serialize(current.tbsCertificate.issuer),
    );

    // Issuer in pool? Walk up.
    let issuerCert: Certificate | null = null;
    for (const candidate of pool) {
      if (chain.includes(candidate)) continue;
      const candidateSubjectDn = new Uint8Array(
        AsnConvert.serialize(candidate.tbsCertificate.subject),
      );
      if (bytesEqual(currentIssuerDn, candidateSubjectDn)) {
        issuerCert = candidate;
        break;
      }
    }

    if (issuerCert) {
      const valid = await verifyCertSignedBy(current, issuerCert);
      if (!valid) {
        return {
          ok: false,
          reason: "chain link signature failed verification",
        };
      }
      chain.push(issuerCert);
      continue;
    }

    // Case (b): issuer not in pool, but matches a trust root's subject DN.
    for (const root of roots) {
      if (!bytesEqual(currentIssuerDn, root.subjectDn)) continue;
      const valid = await verifyCertSignedBy(current, root.cert);
      if (!valid) {
        return {
          ok: false,
          reason: `topmost cert signature failed verification against ${root.name}`,
        };
      }
      return { ok: true, anchor: root.name };
    }

    return {
      ok: false,
      reason: "chain ends at issuer that is not in any embedded trust root",
    };
  }
  return { ok: false, reason: `chain depth exceeded ${MAX_DEPTH} links` };
}

// Find the signer cert in SignedData.certificates by matching SignerInfo.sid
// (issuerAndSerialNumber) against each cert's TBSCertificate.
// - serialNumber is an INTEGER (raw type, compared as BigInt)
// - issuer is a Name (class, compared via DER serialization)
function findSignerCertificate(
  signedData: SignedData,
  signerInfo: SignerInfo,
): Certificate | null {
  if (!signedData.certificates) return null;
  const sid = signerInfo.sid.issuerAndSerialNumber;
  if (!sid) return null;

  const expectedSerial = nonceToBigInt(
    sid.serialNumber as bigint | number | ArrayBuffer | Uint8Array,
  );
  const expectedIssuer = new Uint8Array(AsnConvert.serialize(sid.issuer));

  for (const choice of signedData.certificates) {
    const cert = choice.certificate;
    if (!cert) continue;
    const certSerial = nonceToBigInt(
      cert.tbsCertificate.serialNumber as
        | bigint
        | number
        | ArrayBuffer
        | Uint8Array,
    );
    if (expectedSerial === null || certSerial !== expectedSerial) continue;
    const certIssuer = new Uint8Array(
      AsnConvert.serialize(cert.tbsCertificate.issuer),
    );
    if (!bytesEqual(expectedIssuer, certIssuer)) continue;
    return cert;
  }
  return null;
}

export type TsaFacts = {
  signer: string; // friendly name from the receipt
  algorithmOid: string; // hash algorithm OID found inside TSTInfo
  messageImprintHex: string; // 64-char hex digest the TSA actually signed over
  nonceHex: string; // hex of the TSA's echoed nonce (or empty if absent)
  genTime: string; // ISO 8601 string of TSTInfo.genTime
  pkiStatus: number; // 0 granted, 1 grantedWithMods, 2+ rejection
  signatureVerified: boolean; // true once the CMS signature math passes
  signatureAlgorithm?: string; // OID of the signature algorithm if verified
  chainAnchor?: string; // friendly name of the trust root the chain reached
};

export type TsaCheckOk = {
  ok: true;
  facts: TsaFacts;
};

export type TsaCheckFail = {
  ok: false;
  reason: string;
  facts?: Partial<TsaFacts>;
};

export type TsaCheckResult = TsaCheckOk | TsaCheckFail;

// Verify a TSA entry's content AND cryptographic signature.
//
// Coverage as of slice 2b:
//   - PKIStatus is granted/grantedWithMods
//   - hash algorithm is SHA-256
//   - messageImprint matches receipt's subject.hash
//   - nonce echoed correctly
//   - SignerInfo.messageDigest attribute matches SHA-256(eContent)
//   - CMS SignedData signature is valid (RSASSA-PKCS1-v1_5 + SHA-256 or
//     ECDSA P-256 + SHA-256)
//
// Slice 2c will add cert chain validation to public CA trust anchors.
// Until then `signatureVerified=true` proves the signature math is sound,
// not that the issuer is trusted.
export async function verifyTSAEntry(
  entry: TSAEntry,
  expectedHashHex: string,
): Promise<TsaCheckResult> {
  let respBytes: Uint8Array;
  try {
    respBytes = base64ToBytes(entry.response);
  } catch {
    return { ok: false, reason: "TSA response is not valid base64" };
  }

  let resp: TimeStampResp;
  try {
    resp = AsnConvert.parse(respBytes, TimeStampResp);
  } catch (err) {
    return {
      ok: false,
      reason: `TimeStampResp parse failed: ${(err as Error).message}`,
    };
  }

  const pkiStatus = Number(resp.status.status);
  if (pkiStatus !== 0 && pkiStatus !== 1) {
    return {
      ok: false,
      reason: `TSA returned non-success PKIStatus ${pkiStatus}`,
      facts: { signer: entry.signer, pkiStatus },
    };
  }

  if (!resp.timeStampToken) {
    return {
      ok: false,
      reason: "TSA response carries no timeStampToken",
      facts: { signer: entry.signer, pkiStatus },
    };
  }

  // The timeStampToken is a CMS ContentInfo wrapping a SignedData. The
  // SignedData's encapsulated content is the DER-encoded TSTInfo.
  let signedData: SignedData;
  try {
    const contentInfo = resp.timeStampToken;
    signedData = AsnConvert.parse(contentInfo.content, SignedData);
  } catch (err) {
    return {
      ok: false,
      reason: `SignedData parse failed: ${(err as Error).message}`,
      facts: { signer: entry.signer, pkiStatus },
    };
  }

  const eContent = signedData.encapContentInfo.eContent;
  // For RFC 3161, eContent is a single OCTET STRING containing the
  // DER-encoded TSTInfo. peculiar exposes this as `.single.buffer`.
  const tstInfoBytes = eContent?.single?.buffer;
  if (!tstInfoBytes) {
    return {
      ok: false,
      reason: "SignedData has no encapsulated TSTInfo content",
      facts: { signer: entry.signer, pkiStatus },
    };
  }

  let tstInfo: TSTInfo;
  try {
    tstInfo = AsnConvert.parse(tstInfoBytes, TSTInfo);
  } catch (err) {
    return {
      ok: false,
      reason: `TSTInfo parse failed: ${(err as Error).message}`,
      facts: { signer: entry.signer, pkiStatus },
    };
  }

  // The hash algorithm inside TSTInfo's messageImprint must be SHA-256;
  // anything else is unsupported in Block 1.
  const algOid = tstInfo.messageImprint.hashAlgorithm.algorithm;
  if (algOid !== OID_SHA256) {
    return {
      ok: false,
      reason: `TSTInfo declares unsupported hash algorithm OID ${algOid}`,
      facts: { signer: entry.signer, pkiStatus, algorithmOid: algOid },
    };
  }

  // `.buffer` may be a sliced view into a shared ArrayBuffer; copy via
  // .slice(0) so we read exactly the imprint bytes regardless of upstream
  // memory layout. Cheap insurance — buffer here is 32 bytes.
  const imprintHex = bytesToHex(
    new Uint8Array(tstInfo.messageImprint.hashedMessage.buffer.slice(0)),
  );
  const expectedLower = expectedHashHex.toLowerCase();
  if (imprintHex !== expectedLower) {
    return {
      ok: false,
      reason: `messageImprint mismatch: TSTInfo signed ${imprintHex}, receipt declared ${expectedLower}`,
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
      },
    };
  }

  // Nonce echo: the TSA echoes the nonce we sent in the request. Our
  // receipt stores the original hex; TSTInfo.nonce is an ASN.1 INTEGER
  // (peculiar represents large INTEGERs as a raw byte buffer, big-endian).
  let nonceHex = "";
  if (entry.nonce) {
    if (!/^[0-9a-fA-F]+$/.test(entry.nonce)) {
      return {
        ok: false,
        reason: "receipt nonce is not hex",
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
        },
      };
    }
    const tstNonce = nonceToBigInt(
      tstInfo.nonce as bigint | number | ArrayBuffer | Uint8Array | undefined,
    );
    if (tstNonce === null) {
      return {
        ok: false,
        reason: "receipt has nonce but TSTInfo has none",
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
        },
      };
    }
    const expectedNonce = BigInt(`0x${entry.nonce}`);
    if (expectedNonce !== tstNonce) {
      return {
        ok: false,
        reason: `nonce mismatch: TSTInfo ${tstNonce.toString(16)} vs receipt ${entry.nonce}`,
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
        },
      };
    }
    nonceHex = entry.nonce;
  }

  const genTime = tstInfo.genTime.toISOString();

  // ─── slice 2b: CMS SignedData signature verification ─────────────────
  const signerInfo = signedData.signerInfos[0];
  if (!signerInfo) {
    return {
      ok: false,
      reason: "SignedData has no signerInfos",
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
      },
    };
  }

  const signerCert = findSignerCertificate(signedData, signerInfo);
  if (!signerCert) {
    return {
      ok: false,
      reason: "signer cert not found in SignedData certificates",
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
      },
    };
  }

  // Compute bytes-to-verify. Per CMS § 5.4: when signedAttrs is present, the
  // signature covers the DER-encoded SignedAttributes value with its IMPLICIT
  // [0] tag (0xA0) replaced by the universal SET tag (0x31). When absent,
  // the signature covers eContent directly.
  let bytesToVerify: Uint8Array;
  if (signerInfo.signedAttrs) {
    // Verify the messageDigest attribute matches SHA-256 of TSTInfo bytes.
    const eContentBytes = new Uint8Array(tstInfoBytes);
    const expectedDigest = new Uint8Array(
      await crypto.subtle.digest("SHA-256", eContentBytes as BufferSource),
    );

    let messageDigestSeen: Uint8Array | null = null;
    for (const attr of signerInfo.signedAttrs) {
      if (attr.attrType === OID_MESSAGE_DIGEST) {
        if (attr.attrValues.length === 0) break;
        const value = attr.attrValues[0]!;
        try {
          const oct = AsnConvert.parse(value, OctetString);
          messageDigestSeen = new Uint8Array(oct.buffer.slice(0));
        } catch {
          // attr is malformed — leave messageDigestSeen null
        }
        break;
      }
    }
    if (!messageDigestSeen) {
      return {
        ok: false,
        reason: "signedAttrs missing or has malformed messageDigest attribute",
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
          nonceHex,
          genTime,
          signatureVerified: false,
        },
      };
    }
    if (!bytesEqual(messageDigestSeen, expectedDigest)) {
      return {
        ok: false,
        reason: "messageDigest attribute does not match SHA-256(eContent)",
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
          nonceHex,
          genTime,
          signatureVerified: false,
        },
      };
    }

    // Build the universal SET-tagged DER for the signed attributes by
    // hand. Per CMS §5.4 the bytes-to-sign use the universal SET tag
    // (0x31), not the IMPLICIT [0] tag (0xA0). peculiar's parsed
    // signedAttrs lose their class wrapper after IMPLICIT-context
    // parsing (re-instantiating the class is brittle), so we serialize
    // each Attribute individually and concatenate them in their
    // parsed order. DigiCert/Apple emit DER-canonical SET ordering on
    // the wire, so preserving parse order keeps the byte sequence the
    // signer originally produced.
    const encodedAttrs: Uint8Array[] = [];
    for (const attr of signerInfo.signedAttrs) {
      encodedAttrs.push(new Uint8Array(AsnConvert.serialize(attr)));
    }
    const innerLen = encodedAttrs.reduce((s, a) => s + a.length, 0);
    const inner = new Uint8Array(innerLen);
    let off = 0;
    for (const a of encodedAttrs) {
      inner.set(a, off);
      off += a.length;
    }
    bytesToVerify = derWrap(0x31, inner);
  } else {
    bytesToVerify = new Uint8Array(tstInfoBytes);
  }

  // Choose Web Crypto algorithm.
  const sigOid = signerInfo.signatureAlgorithm.algorithm;
  const digestOid = signerInfo.digestAlgorithm.algorithm;
  const algoSpec = getVerifyAlgorithm(sigOid, digestOid);
  if (!algoSpec) {
    return {
      ok: false,
      reason: `unsupported signature algorithm: sig=${sigOid} digest=${digestOid}`,
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
      },
    };
  }

  // Import the signer cert's public key from its SubjectPublicKeyInfo
  // (DER-encoded SPKI is what crypto.subtle.importKey wants for "spki").
  const spki = AsnConvert.serialize(
    signerCert.tbsCertificate.subjectPublicKeyInfo,
  );
  let publicKey: CryptoKey;
  try {
    publicKey = await crypto.subtle.importKey(
      "spki",
      spki,
      // For RSA-PKCS1: include hash hint for the import.
      algoSpec.algorithm.name === "RSASSA-PKCS1-v1_5"
        ? { name: "RSASSA-PKCS1-v1_5", hash: algoSpec.importHash }
        : { name: "ECDSA", namedCurve: "P-256" },
      false,
      ["verify"],
    );
  } catch (err) {
    return {
      ok: false,
      reason: `public key import failed: ${(err as Error).message}`,
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
        signatureAlgorithm: sigOid,
      },
    };
  }

  let sigBytes: Uint8Array<ArrayBuffer> = new Uint8Array(
    signerInfo.signature.buffer.slice(0),
  );
  // ECDSA-on-CMS signatures are DER-encoded SEQUENCE{r,s}; Web Crypto's
  // ECDSA verify wants raw r||s concatenation. Convert before calling
  // verify; RSA signatures pass through unchanged.
  // namedCurve is locked to P-256 (32-byte coordinates) — see importKey
  // path above. If we add P-384 in a later slice, derive curveBytes from
  // the algorithm spec instead of hardcoding.
  if (algoSpec.algorithm.name === "ECDSA") {
    try {
      sigBytes = derEcdsaToRaw(
        sigBytes,
        algoSpec.curveBytes ?? 32,
      ) as Uint8Array<ArrayBuffer>;
    } catch (err) {
      return {
        ok: false,
        reason: `ECDSA signature DER decode failed: ${(err as Error).message}`,
        facts: {
          signer: entry.signer,
          pkiStatus,
          algorithmOid: algOid,
          messageImprintHex: imprintHex,
          nonceHex,
          genTime,
          signatureVerified: false,
          signatureAlgorithm: sigOid,
        },
      };
    }
  }
  let verified = false;
  try {
    verified = await crypto.subtle.verify(
      algoSpec.algorithm.name === "RSASSA-PKCS1-v1_5"
        ? { name: "RSASSA-PKCS1-v1_5" }
        : { name: "ECDSA", hash: algoSpec.importHash },
      publicKey,
      sigBytes as BufferSource,
      bytesToVerify as BufferSource,
    );
  } catch (err) {
    return {
      ok: false,
      reason: `signature verify error: ${(err as Error).message}`,
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
        signatureAlgorithm: sigOid,
      },
    };
  }

  if (!verified) {
    return {
      ok: false,
      reason: "CMS signature verification failed",
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: false,
        signatureAlgorithm: sigOid,
      },
    };
  }

  // ─── slice 2c: cert chain validation to embedded trust root ──────────
  const certPool: Certificate[] = [];
  if (signedData.certificates) {
    for (const choice of signedData.certificates) {
      if (choice.certificate) certPool.push(choice.certificate);
    }
  }
  const chainResult = await validateChain(signerCert, certPool);
  if (!chainResult.ok) {
    return {
      ok: false,
      reason: `cert chain validation failed: ${chainResult.reason}`,
      facts: {
        signer: entry.signer,
        pkiStatus,
        algorithmOid: algOid,
        messageImprintHex: imprintHex,
        nonceHex,
        genTime,
        signatureVerified: true,
        signatureAlgorithm: sigOid,
      },
    };
  }

  return {
    ok: true,
    facts: {
      signer: entry.signer,
      algorithmOid: algOid,
      messageImprintHex: imprintHex,
      nonceHex,
      genTime,
      pkiStatus,
      signatureVerified: true,
      signatureAlgorithm: sigOid,
      chainAnchor: chainResult.anchor,
    },
  };
}
